import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Briefcase, 
  Sparkles, 
  CheckCircle, 
  Star, 
  Lightbulb, 
  Calendar, 
  ArrowRight,
  MessageSquare,
  GraduationCap,
  Award
} from 'lucide-react';
import SubmissionPopup from '../components/SubmissionPopup';
import MouseFollowEffect from '../components/MouseFollowEffect';
import QuestionNavigator from '../components/QuestionNavigator';

interface FormData {
  name: string;
  email: string;
  company: string;
  answers: Record<string, string | string[]>;
}

export default function ButlerUniversity() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    answers: {}
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showMeetingPopup, setShowMeetingPopup] = useState(false);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const formContainerRef = useRef<HTMLDivElement>(null);

  const questions = [
    {
      id: 'content_goals',
      category: 'Content Goals',
      question: 'Dr. Gentle-Genitty, what are your primary content goals for Butler University\'s College of Education?',
      type: 'select',
      options: [
        'Increasing student enrollment and applications',
        'Enhancing the college\'s academic reputation',
        'Showcasing faculty research and achievements',
        'Improving alumni engagement and donations',
        'Highlighting student success stories',
        'All of the above'
      ]
    },
    {
      id: 'target_audience',
      category: 'Target Audience',
      question: 'Who are the most important audiences you\'re trying to reach with your content?',
      type: 'multiselect',
      options: [
        'Prospective undergraduate students',
        'Prospective graduate students',
        'Parents of prospective students',
        'Current students',
        'Alumni',
        'Academic peers and other institutions',
        'Donors and supporters',
        'Local community'
      ]
    },
    {
      id: 'content_challenges',
      category: 'Content Challenges',
      question: 'What are the biggest challenges Butler University faces with content creation and distribution?',
      type: 'multiselect',
      options: [
        'Consistency in messaging across departments',
        'Limited internal resources for content creation',
        'Difficulty measuring content effectiveness',
        'Standing out among competing institutions',
        'Balancing academic tone with engaging content',
        'Coordinating content across multiple channels',
        'Keeping content fresh and innovative',
        'Other'
      ]
    },
    {
      id: 'content_types',
      category: 'Content Types',
      question: 'Which content types would be most valuable for Butler University\'s College of Education?',
      type: 'multiselect',
      options: [
        'Faculty spotlight videos',
        'Student success stories',
        'Research highlights and publications',
        'Program-specific content',
        'Virtual campus tours',
        'Alumni interviews and testimonials',
        'Educational thought leadership articles',
        'Interactive digital experiences',
        'Social media campaigns'
      ]
    },
    {
      id: 'content_frequency',
      category: 'Content Frequency',
      question: 'How frequently would you ideally like to publish new content?',
      type: 'select',
      options: [
        'Multiple times per week',
        'Weekly',
        'Bi-weekly',
        'Monthly',
        'Quarterly',
        'Varies by channel and content type'
      ]
    },
    {
      id: 'current_investment',
      category: 'Current Investment',
      question: 'What is your current monthly investment in content creation and marketing?',
      type: 'select',
      options: [
        'Under $5,000 per month',
        '$5,000 - $10,000 per month',
        '$10,000 - $20,000 per month',
        '$20,000 - $30,000 per month',
        'Over $30,000 per month'
      ]
    }
  ];

  // Ensure form container is visible when changing steps
  useEffect(() => {
    if (currentStep > 0 && formContainerRef.current) {
      formContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentStep]);

  const handleInputChange = (questionId: string, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionId]: value
      }
    }));
  };

  const handleBasicInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNoteChange = (questionId: string, note: string) => {
    setNotes(prev => ({
      ...prev,
      [questionId]: note
    }));
  };

  const handleNext = () => {
    if (currentStep < questions.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleMultiSelectChange = (questionId: string, option: string) => {
    setFormData(prev => {
      const currentSelections = Array.isArray(prev.answers[questionId]) 
        ? prev.answers[questionId] as string[] 
        : [];
      
      const updatedSelections = currentSelections.includes(option)
        ? currentSelections.filter(item => item !== option)
        : [...currentSelections, option];
      
      return {
        ...prev,
        answers: {
          ...prev.answers,
          [questionId]: updatedSelections
        }
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://hook.eu2.make.com/dn9yy5h7od7y9ds1ka6y34iogwzf2sfk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'butler_university',
          timestamp: new Date().toISOString(),
          name: formData.name,
          email: formData.email,
          company: formData.company,
          answers: formData.answers,
          notes: notes
        }),
      });
      
      if (response.ok) {
        setShowMeetingPopup(true);
      } else {
        console.error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScheduleMeeting = () => {
    window.location.href = 'https://cal.com/dsmarketing.meeting.with.pascoal/butler-university-ds-marketing';
  };

  const handleDeclineMeeting = () => {
    setShowMeetingPopup(false);
    setShowPopup(true);
    // Reset form after successful submission
    setFormData({
      name: '',
      email: '',
      company: '',
      answers: {}
    });
    setNotes({});
    setCurrentStep(0);
  };

  return (
    <div className="page-container pt-20 min-h-screen">
      <div className="content-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <div className="relative">
              <GraduationCap className="w-16 h-16 text-golden animate-pulse" />
              <Sparkles className="w-8 h-8 text-blue-400 absolute -top-2 -right-2 animate-pulse" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Butler <span className="text-golden">University</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Personalized for Dr. Carolyn Gentle-Genitty, Founding Inaugural Dean
          </p>
          <div className="mt-6 max-w-3xl mx-auto bg-black/30 p-6 rounded-xl border border-gray-800">
            <p className="text-gray-300">
              We're impressed by Butler University's commitment to academic excellence and innovation. We'd love to learn more about your content creation needs to help elevate your institution's digital presence.
            </p>
          </div>
        </motion.div>

        <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-8 mb-12 max-w-4xl mx-auto" ref={formContainerRef}>
          <QuestionNavigator isActive={currentStep === 0}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
            >
              <h2 className="text-2xl font-bold mb-6 text-golden">Let's Get Started</h2>
              <p className="text-gray-300 mb-8">
                Please provide some basic information before we dive into the specific questions about Butler University's content creation needs.
              </p>
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Your Name <span className="text-golden">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleBasicInfoChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                    placeholder="Dr. Carolyn Gentle-Genitty"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address <span className="text-golden">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleBasicInfoChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                    placeholder="cgentle@butler.edu"
                  />
                </div>
                
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                    Institution <span className="text-golden">*</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleBasicInfoChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                    placeholder="Butler University"
                  />
                </div>
                
                <div className="pt-4">
                  <button
                    onClick={handleNext}
                    disabled={!formData.name || !formData.email || !formData.company}
                    className="w-full px-6 py-4 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue to Questions
                  </button>
                </div>
              </div>
            </motion.div>
          </QuestionNavigator>

          {questions.map((question, index) => {
            const stepNumber = index + 1;
            return (
              <QuestionNavigator 
                key={question.id} 
                isActive={currentStep === stepNumber}
              >
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-400">
                      Question {stepNumber} of {questions.length}
                    </span>
                    <span className="text-sm font-medium text-golden">
                      {question.category}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold">
                    {question.question}
                  </h3>
                  
                  {question.type === 'select' && (
                    <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2 pb-2">
                      {question.options.map((option) => (
                        <div 
                          key={option}
                          onClick={() => handleInputChange(question.id, option)}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                            formData.answers[question.id] === option
                              ? 'border-golden bg-golden/10'
                              : 'border-gray-800 bg-black/30 hover:border-gray-600'
                          }`}
                        >
                          <div className="flex items-center">
                            <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                              formData.answers[question.id] === option
                                ? 'border-golden'
                                : 'border-gray-600'
                            }`}>
                              {formData.answers[question.id] === option && (
                                <div className="w-3 h-3 rounded-full bg-golden" />
                              )}
                            </div>
                            <span className="text-gray-300">{option}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {question.type === 'multiselect' && (
                    <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2 pb-2">
                      {question.options.map((option) => {
                        const isSelected = Array.isArray(formData.answers[question.id]) && 
                          (formData.answers[question.id] as string[]).includes(option);
                        
                        return (
                          <div 
                            key={option}
                            onClick={() => handleMultiSelectChange(question.id, option)}
                            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                              isSelected
                                ? 'border-golden bg-golden/10'
                                : 'border-gray-800 bg-black/30 hover:border-gray-600'
                            }`}
                          >
                            <div className="flex items-center">
                              <div className={`w-5 h-5 rounded-md border-2 mr-3 flex items-center justify-center ${
                                isSelected
                                  ? 'border-golden'
                                  : 'border-gray-600'
                              }`}>
                                {isSelected && (
                                  <CheckCircle className="w-3 h-3 text-golden" />
                                )}
                              </div>
                              <span className="text-gray-300">{option}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Notes (for internal use only)
                    </label>
                    <div className="flex items-start space-x-2">
                      <MessageSquare className="w-5 h-5 text-gray-400 mt-3" />
                      <textarea
                        value={notes[question.id] || ''}
                        onChange={(e) => handleNoteChange(question.id, e.target.value)}
                        placeholder="Add notes about this response..."
                        className="flex-1 px-4 py-3 rounded-lg bg-black/30 border border-gray-700 focus:outline-none focus:border-gray-500 text-gray-400 text-sm min-h-[80px]"
                      />
                    </div>
                  </div>
                  
                  <div className="pt-4 flex justify-between">
                    <button
                      onClick={handlePrevious}
                      className="px-6 py-3 border-2 border-gray-700 text-gray-300 rounded-full font-medium hover:border-golden hover:text-golden transition-all duration-300"
                    >
                      Previous
                    </button>
                    
                    {stepNumber < questions.length ? (
                      <button
                        onClick={handleNext}
                        disabled={!formData.answers[question.id] || 
                          (Array.isArray(formData.answers[question.id]) && 
                          (formData.answers[question.id] as string[]).length === 0)}
                        className="px-6 py-3 bg-golden text-white rounded-full font-medium hover:bg-golden-600 transition-all duration-300 disabled:opacity-70"
                      >
                        Next Question
                      </button>
                    ) : (
                      <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="px-6 py-3 bg-golden text-white rounded-full font-medium hover:bg-golden-600 transition-all duration-300 disabled:opacity-70"
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Responses'}
                      </button>
                    )}
                  </div>
                </div>
              </QuestionNavigator>
            );
          })}

          <QuestionNavigator isActive={currentStep > questions.length}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="text-center py-8"
            >
              <CheckCircle className="w-16 h-16 text-golden mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-4">Thank You!</h2>
              <p className="text-gray-300 mb-6">
                Your responses have been submitted successfully. We'll be in touch soon with a tailored content strategy for Butler University.
              </p>
            </motion.div>
          </QuestionNavigator>
        </MouseFollowEffect>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-gradient-to-r from-blue-900/30 to-golden/20 rounded-2xl p-8 border border-golden/20">
            <h2 className="text-2xl font-bold mb-4">Why Partner with DS Marketing for Butler University</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-golden mb-2">Higher Education Expertise</h3>
                <p className="text-gray-300">
                  We've helped numerous educational institutions elevate their digital presence with content that resonates with prospective students, current students, alumni, and donors.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-golden mb-2">Comprehensive Content Strategy</h3>
                <p className="text-gray-300">
                  Our approach goes beyond creating individual content pieces. We develop comprehensive strategies that align with your institutional goals and academic mission.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-golden mb-2">Measurable Results</h3>
                <p className="text-gray-300">
                  We focus on delivering content that drives real outcomes—whether that's increased applications, improved alumni engagement, or enhanced institutional reputation.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-golden mb-2">Butler-Specific Approach</h3>
                <p className="text-gray-300">
                  We're impressed by Butler University's commitment to academic excellence and innovation. Our content strategy will be tailored to highlight your unique strengths and achievements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Meeting Popup */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: showMeetingPopup ? 1 : 0 }}
        exit={{ opacity: 0 }}
        className={`fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 ${
          showMeetingPopup ? 'visible' : 'invisible'
        }`}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: showMeetingPopup ? 1 : 0.9, opacity: showMeetingPopup ? 1 : 0 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-black/90 rounded-2xl p-8 max-w-md w-full border border-golden/30"
        >
          <div className="text-center">
            <Award className="w-16 h-16 text-golden mx-auto mb-6" />
            <h2 className="text-2xl font-bold mb-4">
              Elevate Butler University's Content Strategy
            </h2>
            <p className="text-lg text-gray-300 mb-6">
              Thank you for sharing your insights, Dr. Gentle-Genitty. Based on your responses, we've identified several opportunities to enhance Butler University's content strategy.
            </p>
            <p className="text-lg text-gray-300 mb-6">
              Would you like to schedule a personalized strategy session where we can discuss:
            </p>
            <ul className="text-left mb-6 space-y-2">
              <li className="flex items-start">
                <Lightbulb className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                <span>A custom content plan aligned with your institutional goals</span>
              </li>
              <li className="flex items-start">
                <Lightbulb className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                <span>Strategies to maximize your content investment</span>
              </li>
              <li className="flex items-start">
                <Lightbulb className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                <span>Innovative approaches that will set Butler apart</span>
              </li>
            </ul>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleScheduleMeeting}
                className="px-6 py-3 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300"
              >
                Yes, I Want to Get Started Right Away
              </button>
              <button
                onClick={handleDeclineMeeting}
                className="px-6 py-3 border border-gray-600 text-gray-300 rounded-full font-semibold hover:border-gray-400 hover:text-white transition-all duration-300"
              >
                Not Right Now
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
      
      <SubmissionPopup 
        isOpen={showPopup} 
        onClose={() => setShowPopup(false)} 
        message="Thank you for your time Dr. Gentle-Genitty! You will be redirected to schedule your meeting shortly."
        redirectUrl="https://cal.com/dsmarketing.meeting.with.pascoal/butler-university-ds-marketing"
        isLeo={false}
      />
    </div>
  );
}